/**
 * Audit Vault — Phase 1 redesign.
 *
 * The Vault is now organised as a 9-tile grid that mirrors the Site
 * Compliance Assessment:
 *
 *   • Monthly tiles  (Hygiene / Cleanliness / CCP)
 *       → 12-month calendar row, each box coloured by the latest snapshot's
 *         status (approved · awaiting · rejected · missing · N/A).
 *
 *   • Event tiles    (CCP Deviations / Incidents & NCR)
 *       → total · filed · open badges; "0" is the perfect-site reading.
 *
 *   • Standing tiles (Training / Calibration / FSMS Team / Documentation)
 *       → count of standing snapshots filed.
 *
 * Clicking a tile expands the per-tile chronological list of records,
 * which is where all the existing sign / approve / reject / discard /
 * download actions live.
 *
 * Master controls at the top:
 *   • Site filter — defaults to "All sites"; flips every tile to that site.
 *   • Year selector — auditors think in calendar years; default is current.
 *   • "Download audit-pack ZIP" (placeholder for the backlog feature).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";
import {
  FolderArchive, Clock, CheckCircle2, XCircle, AlertTriangle, FileText,
  Download, PenLine, ShieldCheck, RefreshCw, Trash2, Send, Layers,
  Sparkles, ClipboardCheck, Thermometer, Siren, GraduationCap,
  Wrench, Users, BookOpen, ChevronDown, ChevronUp, Package, History, RotateCcw,
  Building2, MapPin,
} from "lucide-react";

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const TILE_ICONS = {
  hygiene: ClipboardCheck,
  cleanliness: Sparkles,
  ccp: Thermometer,
  deviations: AlertTriangle,
  incidents: Siren,
  training: GraduationCap,
  calibration: Wrench,
  fsms_team: Users,
  documentation: BookOpen,     // legacy — kept for back-compat if dashboard ever returns it
  pest_control: BookOpen,
  approved_vendor: Package,
  kitchen_daily: Thermometer,  // Cooking + Hot & Cold Hold combined tile
  equipment_care: Wrench,       // Cleaning + Maintenance combined monthly log
};

const STATUS_META = {
  awaiting_generator_sig: { label: "Awaiting generator", icon: Clock,   cls: "warn" },
  awaiting_approval:      { label: "Awaiting approval",  icon: PenLine, cls: "warn" },
  approved:               { label: "Approved",           icon: CheckCircle2, cls: "ok" },
  needs_revision:         { label: "Returned for rework", icon: RotateCcw, cls: "warn" },
  // Legacy state — pre-2026-06-23 records. Treated like needs_revision
  // in the UI (badge + Resubmit button) but kept distinct in case any
  // old data still exists post-migration.
  rejected:               { label: "Returned for rework", icon: RotateCcw, cls: "warn" },
  superseded:             { label: "Superseded",         icon: Layers,  cls: "" },
};

// Map a month-cell status to a one-line colour token used by the grid.
const monthCellColor = (status) => {
  switch (status) {
    case "approved":               return { bg: "var(--vb-ok, #22c55e)",   fg: "#fff", title: "Approved" };
    case "awaiting_approval":      return { bg: "#f59e0b", fg: "#fff", title: "Awaiting approval" };
    case "awaiting_generator_sig": return { bg: "#fbbf24", fg: "#1f2937", title: "Awaiting generator" };
    case "needs_revision":
    case "rejected":               return { bg: "var(--vb-error, #ef4444)", fg: "#fff", title: "Returned for rework" };
    case "na":                     return { bg: "transparent", fg: "rgba(0,0,0,0.25)", title: "Site didn't exist", dashed: true };
    default:                       return { bg: "var(--vb-bg-2, #f3f4f6)", fg: "rgba(0,0,0,0.45)", title: "Not filed" };
  }
};

export default function AuditVault() {
  const { user } = useAuth();
  const { notify, confirm } = useDialog();
  // Site selection is driven exclusively by the AppShell master site
  // filter (SiteContext). The Vault used to expose its own dropdown
  // which drifted from the header — auditors would silently pick
  // different sites in each control and end up staring at mismatched
  // data. Now the Vault reads `activeSiteId` and shows a locked chip
  // so there's a single source of truth.
  const { activeSiteId, isAllSites, activeSite } = useSite();
  const siteId = isAllSites ? "" : activeSiteId;
  const [sites, setSites] = useState([]);
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());          // tile.keys currently expanded
  const [tileRecords, setTileRecords] = useState({});      // {tileKey: [records...]}
  const [tileLoading, setTileLoading] = useState({});      // {tileKey: bool}
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);
  const [trailOpen, setTrailOpen] = useState(false);
  const [trail, setTrail] = useState([]);
  const [trailLoading, setTrailLoading] = useState(false);

  const canApprove = ["owner", "manager"].includes(user?.role);
  const isOwner = user?.role === "owner";

  // ─── Initial data ────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sites");
        setSites(data.sites || []);
      } catch (e) {
        console.debug("Vault sites load failed:", e);
      }
    })();
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year) });
      if (siteId) params.append("site_id", siteId);
      const { data } = await api.get(`/audit-vault/dashboard?${params.toString()}`);
      setDashboard(data);
      // NOTE: do NOT wipe `tileRecords` here. This callback is invoked from
      // every action handler (sign / approve / reject / delete) via
      // `refreshTile`, and wiping the per-tile cache made every expanded
      // tile fall back to "No records for X" until the user toggled it
      // again. Cache invalidation on site/year change lives in the
      // dedicated useEffect below.
    } catch (e) {
      await notify({ title: "Couldn't load vault", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setLoading(false); }
  }, [siteId, year, notify]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  // When the user changes site or year the cached per-tile lists are
  // genuinely stale → invalidate them so the next expand re-fetches.
  // Triggers ONCE per scope change, not after every action.
  useEffect(() => { setTileRecords({}); }, [siteId, year]);

  // ─── Per-tile records (lazy) ─────────────────────────────────────────────
  // `force=true` is passed by `refreshTile` after an action — it bypasses
  // the cache-hit early return, which would otherwise see the *stale*
  // closure value of `tileRecords` (React batches our `setTileRecords`
  // delete) and skip the refetch entirely. That was the cause of "row
  // disappears after sign-off, page refresh brings it back".
  const loadTileRecords = async (tile, { force = false } = {}) => {
    if (!force && tileRecords[tile.key]) return;
    setTileLoading((m) => ({ ...m, [tile.key]: true }));
    try {
      // For each report_type in this tile, fetch the year-scoped list and
      // merge. The backend /audit-vault endpoint already supports type
      // and year filters.
      const all = [];
      for (const rt of (tile.report_types || [])) {
        const params = new URLSearchParams({ report_type: rt, year: String(year) });
        if (siteId) params.append("site_id", siteId);
        const { data } = await api.get(`/audit-vault?${params.toString()}`);
        all.push(...(data.reports || []));
      }
      // Newest first.
      all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setTileRecords((m) => ({ ...m, [tile.key]: all }));
    } catch (e) {
      await notify({ title: "Couldn't load records", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally {
      setTileLoading((m) => ({ ...m, [tile.key]: false }));
    }
  };

  // ─── Audit Pack ZIP export ───────────────────────────────────────────────
  // Bundles every APPROVED PDF for the selected year into a Site → Month →
  // Category → file zip. Streams from the backend so no polling / job needed.
  const [zipBusy, setZipBusy] = useState(false);
  const downloadAuditPack = async () => {
    if (zipBusy) return;
    setZipBusy(true);
    try {
      const res = await api.get(`/audit-vault/audit-pack.zip?year=${year}`, {
        responseType: "blob",
      });
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      const filename = match?.[1] || `VanguardBeacon_AuditPack_${year}.zip`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      let detail = "";
      try {
        if (e.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          detail = parsed?.detail?.message || parsed?.detail || "";
        } else {
          detail = formatErr(e.response?.data?.detail);
        }
      } catch { /* fall through */ }
      await notify({
        title: "Couldn't build audit pack",
        message: detail || "Something went wrong. Try again in a moment.",
        variant: "err",
      });
    } finally {
      setZipBusy(false);
    }
  };


  const toggleExpanded = (tile) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(tile.key)) {
        next.delete(tile.key);
      } else {
        next.add(tile.key);
        // Lazy-load records the first time this tile opens.
        loadTileRecords(tile);
      }
      return next;
    });
  };

  // ─── Actions on individual records (delegate to existing endpoints) ──────
  const refreshTile = async (tile) => {
    // `force: true` — without it, the closure inside `loadTileRecords`
    // still sees the cached array and short-circuits.
    await loadTileRecords(tile, { force: true });
    await loadDashboard();
  };
  const onSignGenerator = async (tile, rec) => {
    const ok = await confirm({
      title: "Sign as generator?",
      message: `Your signature will be embedded in "${rec.title}". The report then moves to "Awaiting approval".`,
    });
    if (!ok) return;
    try {
      await api.post(`/audit-vault/${rec.id}/sign-generator`, {});
      await refreshTile(tile);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "signature_required") {
        await notify({ title: "Signature required", message: det.message, variant: "warn" });
      } else {
        await notify({ title: "Couldn't sign", message: formatErr(det), variant: "err" });
      }
    }
  };
  const onApprove = async (tile, rec) => {
    const ok = await confirm({
      title: "Approve & lock report?",
      message: `Your signature will be embedded and "${rec.title}" will be locked as the audit-of-record. This cannot be undone.`,
      confirmLabel: "Approve & lock",
    });
    if (!ok) return;
    try {
      await api.post(`/audit-vault/${rec.id}/sign-approver`, {});
      await refreshTile(tile);
    } catch (e) {
      const det = e.response?.data?.detail;
      const msg = typeof det === "object" ? det.message : formatErr(det);
      await notify({ title: "Couldn't approve", message: msg, variant: "err" });
    }
  };
  const onDelete = async (tile, rec) => {
    const locked = rec.immutable || rec.status === "approved";
    const ok = await confirm({
      title: locked ? "Force-delete approved report?" : "Discard this draft?",
      message: locked
        ? (
            `"${rec.title}" is APPROVED and locked. Deleting it breaks the audit chain — ` +
            `this should only be done if the underlying data was wrong and the report ` +
            `must be regenerated. The deletion is logged in the audit trail.`
          )
        : `"${rec.title}" will be removed from the vault. This is only allowed before approval.`,
      confirmLabel: locked ? "Force delete" : "Discard",
    });
    if (!ok) return;
    try {
      const qs = locked ? "?force=true" : "";
      await api.delete(`/audit-vault/${rec.id}${qs}`);
      await refreshTile(tile);
    } catch (e) {
      const det = e.response?.data?.detail;
      const msg = (det && typeof det === "object" && det.message) ? det.message : formatErr(det);
      await notify({ title: "Couldn't delete", message: msg, variant: "err" });
    }
  };
  const submitReject = async () => {
    if (!rejectFor || rejectReason.trim().length < 3) return;
    setRejectBusy(true);
    try {
      await api.post(`/audit-vault/${rejectFor.id}/reject`, { reason: rejectReason.trim() });
      const tile = (dashboard?.tiles || []).find((t) => t.key === rejectFor.tileKey);
      if (tile) await refreshTile(tile);
      setRejectFor(null);
      setRejectReason("");
    } catch (e) {
      await notify({ title: "Couldn't reject", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setRejectBusy(false); }
  };
  const onDownload = (rec) => {
    const a = document.createElement("a");
    a.href = `${process.env.REACT_APP_BACKEND_URL}/api/v1/audit-vault/${rec.id}/pdf`;
    a.target = "_blank";
    a.rel = "noopener";
    a.click();
  };

  // ─── Activity log (audit trail) ─────────────────────────────────────────
  const openTrail = async () => {
    setTrailOpen(true);
    if (trail.length > 0) return;
    setTrailLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (siteId) params.append("site_id", siteId);
      const { data } = await api.get(`/audit-vault/activity?${params.toString()}`);
      setTrail(data.events || []);
    } catch (e) {
      await notify({ title: "Couldn't load activity", message: formatErr(e.response?.data?.detail), variant: "err" });
    } finally { setTrailLoading(false); }
  };
  // Invalidate the cached trail whenever the filters change so re-opening
  // the panel always reflects the latest activity for the chosen scope.
  useEffect(() => { setTrail([]); }, [siteId, year]);

  // ─── Year selector options (current year + 4 previous) ───────────────────
  const yearOptions = useMemo(() => {
    const now = new Date().getUTCFullYear();
    return Array.from({ length: 5 }, (_, i) => now - i);
  }, []);

  return (
    <div className="vb-fade-in" data-testid="audit-vault-page">
      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3 mb-3">
        <FolderArchive size={26} style={{ color: "var(--vb-accent)" }} className="mt-1 shrink-0" />
        <div>
          <h1 className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>Audit Vault</h1>
          <p className="text-[13px] opacity-70 m-0 mt-1 max-w-3xl">
            Your once-a-year audit pack. The grid below mirrors the Site Compliance Assessment —
            each tile shows whether every required report has been filed, signed and approved.
            Click a tile for the chronological list and sign-off actions.
          </p>
        </div>
      </div>

      {/* ─── Master filters ─────────────────────────────────────────────── */}
      <div className="vb-card p-3 mb-5 flex flex-wrap items-end gap-3" data-testid="vault-controls">
        <div className="min-w-0">
          <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wider opacity-70">Site</label>
          <div
            className="vb-input flex items-center gap-2 cursor-not-allowed"
            style={{ opacity: siteId ? 1 : 0.65, background: "var(--vb-bg-2)" }}
            title="Change the active site from the header filter at the top of the app."
            data-testid="vault-site-filter"
          >
            <MapPin size={14} className="shrink-0" style={{ color: "var(--vb-text-2)" }} />
            <span className="truncate">
              {siteId
                ? (activeSite?.name || sites.find((s) => s.id === siteId)?.name || "—")
                : "All sites (change in header)"}
            </span>
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wider opacity-70">Audit year</label>
          <select className="vb-input" value={year}
            onChange={(e) => setYear(Number(e.target.value))} data-testid="vault-year">
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button className="vb-btn ghost sm" onClick={loadDashboard} disabled={loading}
          data-testid="vault-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button className="vb-btn ghost sm" onClick={openTrail}
            data-testid="vault-activity-open"
            title="See every generate / sign / approve / reject / delete event in this Vault — the answer to 'why was this report regenerated?'">
            <History size={12} /> Activity log
          </button>
          <button className="vb-btn ghost sm" onClick={downloadAuditPack}
            disabled={zipBusy}
            title="Bundle every APPROVED PDF for the selected year into one ZIP, organised by Site → Month → Category. Partial years are supported."
            data-testid="vault-zip-download">
            <Package size={12} className={zipBusy ? "animate-pulse" : ""} />
            {zipBusy ? "Building…" : "Download Audit Pack (ZIP)"}
          </button>
        </div>
      </div>

      {/* ─── Tiles ──────────────────────────────────────────────────────── */}
      {loading && <div className="text-center py-10 opacity-60" data-testid="vault-loading">Loading…</div>}
      {!loading && dashboard && (
        <div className="grid grid-cols-1 gap-3">
          {dashboard.tiles.map((tile) => (
            <VaultTile
              key={tile.key}
              tile={tile}
              expanded={expanded.has(tile.key)}
              onToggle={() => toggleExpanded(tile)}
              records={tileRecords[tile.key]}
              recordsLoading={!!tileLoading[tile.key]}
              user={user}
              canApprove={canApprove}
              isOwner={isOwner}
              sites={sites}
              onSignGenerator={(rec) => onSignGenerator(tile, rec)}
              onApprove={(rec) => onApprove(tile, rec)}
              onReject={(rec) => { setRejectFor({ id: rec.id, title: rec.title, generatorName: rec.generator?.name, tileKey: tile.key }); setRejectReason(""); }}
              onDelete={(rec) => onDelete(tile, rec)}
              onDownload={onDownload}
            />
          ))}
        </div>
      )}

      {/* ─── Reject modal ───────────────────────────────────────────────── */}
      {rejectFor && (
        <div className="vb-modal-bg" data-testid="vault-reject-modal"
          onClick={() => !rejectBusy && setRejectFor(null)}>
          <div className="vb-modal" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480 }}>
            <h3 className="vb-heading text-lg m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
              Return report for revision
            </h3>
            <p className="text-[13px] opacity-80 mb-3">
              <strong>{rejectFor.generatorName}</strong> will be notified that
              &ldquo;{rejectFor.title}&rdquo; needs changes. They can regenerate a new version.
            </p>
            <label className="block text-[12px] font-medium mb-1">Reason (required)</label>
            <textarea className="vb-input w-full" rows={4}
              value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Day 17 readings are still missing for Site Orchard…"
              data-testid="vault-reject-reason" autoFocus />
            <div className="flex justify-end gap-2 mt-3">
              <button className="vb-btn sm ghost" onClick={() => setRejectFor(null)}
                disabled={rejectBusy}>Cancel</button>
              <button className="vb-btn sm" onClick={submitReject}
                disabled={rejectBusy || rejectReason.trim().length < 3}
                data-testid="vault-reject-submit">
                {rejectBusy ? "Sending…" : "Send rejection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Activity Log modal ─────────────────────────────────────────── */}
      {trailOpen && (
        <div className="vb-modal-bg" onClick={() => setTrailOpen(false)}
          data-testid="vault-activity-modal">
          <div className="vb-modal" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 720, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h3 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>
                  <History size={16} className="inline -mt-0.5 mr-1" /> Vault activity log
                </h3>
                <p className="text-[12px] opacity-70 m-0 mt-1">
                  Every generate, sign, approve, reject and delete event for the selected scope —
                  the full chain of custody for auditors. {siteId ? "" : "All sites shown."}
                </p>
              </div>
              <button className="vb-btn xs ghost" onClick={() => setTrailOpen(false)}>Close</button>
            </div>
            <div className="overflow-y-auto -mx-2 px-2" style={{ flex: 1 }}>
              {trailLoading && <div className="text-center py-6 opacity-60 text-sm">Loading…</div>}
              {!trailLoading && trail.length === 0 && (
                <div className="text-center py-6 opacity-60 text-sm" data-testid="vault-activity-empty">
                  No Vault activity yet for this scope.
                </div>
              )}
              {!trailLoading && trail.length > 0 && (
                <ul className="grid gap-1.5">
                  {trail.map((ev, i) => <TrailRow key={ev.id || i} ev={ev} />)}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TRAIL_META = {
  "audit_vault.generated":  { label: "Snapshot generated",     icon: FileText,    cls: "" },
  "audit_vault.gen_signed": { label: "Generator signed",       icon: PenLine,     cls: "warn" },
  "audit_vault.approved":   { label: "Approved & locked",      icon: CheckCircle2, cls: "ok" },
  "audit_vault.rejected":   { label: "Returned for revision",  icon: Send,        cls: "warn" },
  "audit_vault.deleted":    { label: "Deleted",                icon: Trash2,      cls: "err" },
};

function TrailRow({ ev }) {
  const meta = TRAIL_META[ev.action] || { label: ev.action, icon: AlertTriangle, cls: "" };
  const Icon = meta.icon;
  const p = ev.payload || {};
  const force = p.force === true;
  const when = (ev.created_at || "").replace("T", " ").slice(0, 16);
  return (
    <li className="rounded-md p-2 flex items-start gap-2"
      style={{
        background: "var(--vb-bg-2, #faf6f1)",
        border: "1px solid var(--vb-border, rgba(0,0,0,0.06))",
      }}
      data-testid={`trail-row-${ev.id}`}>
      <Icon size={14} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`vb-pill ${meta.cls} text-[10px]`}>{meta.label}</span>
          {force && (
            <span className="vb-pill err text-[10px]" title="Owner used Force-delete on an approved record">
              FORCE
            </span>
          )}
          <span className="text-[12px] truncate" style={{ color: "var(--vb-text-2)" }}>
            {p.title || p.report_type || ev.target_id}
          </span>
        </div>
        <div className="text-[11px] opacity-60 mt-0.5">
          <strong>{ev.actor_email || "—"}</strong>
          {ev.actor_role && <> · <em>{ev.actor_role}</em></>}
          {" · "}{when}
          {p.status_at_delete && <> · was <em>{p.status_at_delete}</em></>}
          {p.reason && <> · reason: <em>&ldquo;{p.reason}&rdquo;</em></>}
        </div>
      </div>
    </li>
  );
}

// ─── Tile ─────────────────────────────────────────────────────────────────
function VaultTile({ tile, expanded, onToggle, records, recordsLoading, user, canApprove, isOwner, sites,
                    onSignGenerator, onApprove, onReject, onDelete, onDownload }) {
  const Icon = TILE_ICONS[tile.key] || FileText;
  const filable = tile.filing_supported !== false;
  return (
    <section className="vb-card p-4" data-testid={`vault-tile-${tile.key}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
          style={{ background: "var(--vb-bg-2)", color: "var(--vb-text-2)" }}>
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="vb-heading text-base m-0" style={{ color: "var(--vb-text-2)" }}>{tile.label}</h3>
          <p className="text-[11px] opacity-60 m-0">{tileSummary(tile)}</p>
        </div>
        {!filable && (
          <span className="vb-pill" style={{ background: "var(--vb-bg-2)" }} title="Vault filing for this pillar lands in a later phase">
            Soon
          </span>
        )}
      </div>

      {tile.kind === "monthly" && <MonthlyGrid months={tile.months} />}
      {tile.kind === "events"  && <EventsBadges ev={tile.events} />}
      {tile.kind === "standing" && <StandingBadge st={tile.standing} />}

      {/* Expand toggle — only if the tile actually has filable types */}
      {(tile.report_types?.length || 0) > 0 && (
        <button className="vb-btn ghost sm mt-3 w-full justify-center"
          onClick={onToggle}
          data-testid={`vault-tile-expand-${tile.key}`}>
          {expanded ? <><ChevronUp size={14} /> Hide records</> : <><ChevronDown size={14} /> Show records</>}
        </button>
      )}

      {expanded && (
        <div className="mt-3 space-y-2 border-t pt-3" style={{ borderColor: "var(--vb-border, rgba(0,0,0,0.08))" }}>
          {recordsLoading && <div className="text-center text-xs opacity-60 py-3">Loading…</div>}
          {!recordsLoading && (!records || records.length === 0) && (
            <div className="text-center text-xs opacity-60 py-3" data-testid={`vault-tile-empty-${tile.key}`}>
              No records for {tile.label} in this period.{" "}
              {filable && (
                <Link to="/app/reports" className="vb-link">Generate one from Reports →</Link>
              )}
            </div>
          )}
          {!recordsLoading && (records || []).map((rec) => (
            <VaultRow key={rec.id} rec={rec}
              isGenerator={rec.initiated_by?.user_id === user?.id}
              canApprove={canApprove}
              isOwner={isOwner}
              sites={sites}
              onSignGenerator={() => onSignGenerator(rec)}
              onApprove={() => onApprove(rec)}
              onReject={() => onReject(rec)}
              onDelete={() => onDelete(rec)}
              onDownload={() => onDownload(rec)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function tileSummary(tile) {
  if (tile.kind === "monthly") {
    const ok = (tile.months || []).filter((m) => m.status === "approved").length;
    const inflight = (tile.months || []).filter((m) => m.status === "awaiting_approval" || m.status === "awaiting_generator_sig").length;
    const missing = (tile.months || []).filter((m) => m.status === "missing" || m.status === "rejected").length;
    return `${ok} approved · ${inflight} in flight · ${missing} missing`;
  }
  if (tile.kind === "events") {
    const ev = tile.events || {};
    return `${ev.total} ${ev.label || "events"} · ${ev.filed} filed`;
  }
  const st = tile.standing || {};
  return `${st.count} ${st.label || "records"}`;
}

function MonthlyGrid({ months }) {
  return (
    <div className="grid grid-cols-12 gap-1" data-testid="monthly-grid">
      {(months || []).map((m) => {
        const c = monthCellColor(m.status);
        return (
          <div key={m.month}
            title={`${MONTH_ABBR[m.month - 1]} — ${c.title}`}
            className="rounded text-[10px] font-semibold text-center py-1.5 select-none"
            style={{
              background: c.bg,
              color: c.fg,
              border: c.dashed ? "1px dashed rgba(0,0,0,0.15)" : "1px solid rgba(0,0,0,0.05)",
              opacity: m.status === "na" ? 0.5 : 1,
            }}
            data-testid={`month-cell-${m.month}-${m.status}`}>
            {MONTH_ABBR[m.month - 1].slice(0, 1)}
          </div>
        );
      })}
    </div>
  );
}

function EventsBadges({ ev }) {
  if (!ev) return null;
  const total = ev.total || 0;
  const filed = ev.filed || 0;
  const open = Math.max(0, total - filed);
  const perfect = ev.perfect_when_zero && total === 0;
  return (
    <div className="grid grid-cols-3 gap-1 text-center" data-testid="events-badges">
      <Badge value={total} label={ev.label || "events"} tone={perfect ? "ok" : "neutral"} />
      <Badge value={filed} label="in vault" tone="info" />
      <Badge value={open} label="not filed" tone={open === 0 ? "ok" : "warn"} />
    </div>
  );
}

function StandingBadge({ st }) {
  if (!st) return null;
  return (
    <div className="text-center py-1" data-testid="standing-badge">
      <div className="text-2xl font-bold" style={{ color: "var(--vb-text-2)" }}>{st.count || 0}</div>
      <div className="text-[11px] opacity-60">{st.label || "records"}</div>
    </div>
  );
}

function Badge({ value, label, tone }) {
  const bg = tone === "ok"   ? "rgba(34,197,94,0.10)"
           : tone === "warn" ? "rgba(245,158,11,0.10)"
           : tone === "info" ? "var(--vb-bg-2, #f3f4f6)"
           :                   "var(--vb-bg-2, #f3f4f6)";
  return (
    <div className="rounded-md py-2" style={{ background: bg }}>
      <div className="text-lg font-bold leading-none" style={{ color: "var(--vb-text-2)" }}>{value}</div>
      <div className="text-[10px] opacity-70 mt-0.5">{label}</div>
    </div>
  );
}

// ─── Per-record row (used inside expanded tile bodies) ───────────────────
function VaultRow({ rec, isGenerator, canApprove, isOwner, sites, onSignGenerator, onApprove, onReject, onDelete, onDownload }) {
  const status = STATUS_META[rec.status] || { label: rec.status, icon: AlertTriangle, cls: "warn" };
  const periodLabel = rec.period?.month
    ? `${String(rec.period.year)}-${String(rec.period.month).padStart(2, "0")}`
    : rec.period?.year || "—";
  // Resolve the site label for this record. Records with an explicit
  // `site_id` show the site name; multi-site rollups (CCP, tenant-wide
  // reports) show "All sites" so the user always knows the scope.
  const siteLabel = rec.site_id
    ? ((sites || []).find((s) => s.id === rec.site_id)?.name || "Unknown site")
    : "All sites";
  const showSignGenerator = rec.status === "awaiting_generator_sig" && isGenerator;
  const showApprove = rec.status === "awaiting_approval" && canApprove && !isGenerator;
  // "Returned for rework" = either the new `needs_revision` or the
  // legacy `rejected` state. Any generator/supervisor can resubmit
  // (per product decision — original filer may have left the team).
  const needsRevision = rec.status === "needs_revision" || rec.status === "rejected";
  const isFinal = rec.status === "approved";
  // Discard / Delete rules:
  //  • Draft awaiting generator-sig → originator (or owner) can discard.
  //  • Awaiting approval / needs-revision → owner can withdraw via the same flow.
  //  • Approved & locked → owner only, with a strong "force-delete" warning so
  //    they can wipe a bad snapshot and regenerate a clean one.
  const showDiscard =
    (rec.status === "awaiting_generator_sig" && (isGenerator || isOwner)) ||
    ((rec.status === "awaiting_approval" || needsRevision) && isOwner) ||
    (isFinal && isOwner);
  const deleteLabel = isFinal ? "Force delete" : "Discard";
  return (
    <div className="rounded-md p-3" data-testid={`vault-row-${rec.id}`}
      style={{
        background: "var(--vb-bg, #fff)",
        borderLeft: isFinal ? "3px solid var(--vb-accent)"
                  : needsRevision ? "3px solid var(--vb-error, #ef4444)"
                  : "3px solid transparent",
        border: "1px solid var(--vb-border, rgba(0,0,0,0.06))",
      }}>
      {/* Info row — title + meta + pills stack on top, full-width */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <FileText size={13} style={{ color: "var(--vb-accent)" }} className="shrink-0" />
          <h4 className="vb-heading text-sm m-0 break-words" style={{ color: "var(--vb-text-2)" }}>{rec.title}</h4>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <span className={`vb-pill ${status.cls} text-[10px] whitespace-nowrap`}>
            <status.icon size={10} /> {status.label}
          </span>
          <span className="vb-pill text-[10px] whitespace-nowrap" style={{ background: "var(--vb-bg-2,#faf6f1)" }}
            title="Site scope of this record" data-testid={`vault-row-site-${rec.id}`}>
            <Building2 size={10} /> {siteLabel}
          </span>
          <span className="vb-pill text-[10px] whitespace-nowrap" style={{ background: "var(--vb-bg-2,#faf6f1)" }}>
            <Clock size={10} /> {periodLabel}
          </span>
          {rec.immutable && (
            <span className="vb-pill ok text-[10px] whitespace-nowrap">
              <ShieldCheck size={10} /> Locked
            </span>
          )}
        </div>
        <div className="text-[11px] opacity-70">
          Initiated by <strong>{rec.initiated_by?.name}</strong>
          {" · "}{(rec.created_at || "").slice(0, 10)}
          {rec.generator && <>{" · "}generator: <strong>{rec.generator.name}</strong></>}
          {rec.approver  && <>{" · "}approver: <strong>{rec.approver.name}</strong></>}
        </div>
        {rec.rejection && needsRevision && (
          <div className="text-[11px] mt-1 p-2 rounded"
            style={{
              color: "var(--vb-text-2)",
              background: "rgba(239,68,68,0.06)",
              borderLeft: "2px solid var(--vb-error, #ef4444)",
            }}>
            <strong style={{ color: "var(--vb-error)" }}>Returned by {rec.rejection.by_name}:</strong>
            <em> &ldquo;{rec.rejection.reason}&rdquo;</em>
            <div className="opacity-60 mt-0.5">
              Open <Link to="/app/reports" className="font-semibold hover:underline" style={{ color: "var(--vb-text-2)" }}>Reports → Critical Task List</Link> to fix the data and file a fresh version. This record will supersede automatically once the new one is approved.
            </div>
          </div>
        )}
      </div>
      {/* Actions row — sits below the info, right-aligned, wraps on narrow */}
      <div className="flex gap-1.5 flex-wrap justify-end mt-2 pt-2"
        style={{ borderTop: "1px dashed var(--vb-border, rgba(0,0,0,0.06))" }}>
        <button className="vb-btn sm ghost" onClick={onDownload}
          data-testid={`vault-download-${rec.id}`}>
          <Download size={13} /> PDF
        </button>
        {showSignGenerator && (
          <button className="vb-btn sm" onClick={onSignGenerator}
            data-testid={`vault-sign-gen-${rec.id}`}>
            <PenLine size={13} /> Sign
          </button>
        )}
        {showApprove && (
          <>
            <button className="vb-btn sm" onClick={onApprove}
              data-testid={`vault-approve-${rec.id}`}>
              <CheckCircle2 size={13} /> Approve
            </button>
            <button className="vb-btn sm ghost" onClick={onReject}
              data-testid={`vault-reject-${rec.id}`}>
              <Send size={13} /> Return
            </button>
          </>
        )}
        {needsRevision && (
          <span
            className="text-[10px] opacity-70 self-center inline-flex items-center gap-1"
            title="Open the Reports page → Critical Task List to file a fresh report. The returned version supersedes automatically."
            data-testid={`vault-needs-revision-hint-${rec.id}`}
          >
            <AlertTriangle size={10} /> Fix data on Reports page
          </span>
        )}
        {showDiscard && (
          <button
            className={`vb-btn sm ${isFinal ? "" : "ghost"}`}
            onClick={onDelete}
            style={isFinal ? { background: "var(--vb-error, #ef4444)", color: "#fff" } : undefined}
            data-testid={`vault-discard-${rec.id}`}
            title={isFinal
              ? "Force-delete this approved report so it can be regenerated. The deletion is logged."
              : "Discard this draft."}
          >
            <Trash2 size={13} /> {deleteLabel}
          </button>
        )}
      </div>
    </div>
  );
}
